import type { HumanVerification, VerificationStatus } from '@clm/shared-types';

import { VERIFICATION_STATUS_COPY } from './trust-labels';

/**
 * The human half of the CR-01 pairing, as a badge — and, since CLM-108, the
 * policy half beside it.
 *
 * `HumanVerificationSchema` is discriminated on `is_human_verified`, which is
 * what makes a verified state impossible to represent without a verifier and a
 * timestamp. Every branch is rendered here and the verifier is named in the
 * accessible name of the verified one, because "who confirmed this" is the
 * evidence, not decoration.
 *
 * `Verified` and `Corrected` are kept visibly apart. They are the correction
 * rate NFR-12 measures, and a single "done" badge would erase the only accuracy
 * signal this system generates for free.
 *
 * ## `AutoApproved` is settled, and nobody read it (CLM-108, CR-06)
 *
 * The `false` member is itself a union on `verification_status` (R-108-15), so
 * this function must handle the policy member to compile. It reads
 * **"Auto-approved (policy)"** in the `ai` tone — never `ok`, the verified
 * colour — and its accessible name says outright that no person reviewed it,
 * with the instant of the `AutoApproval` audit row. Rendering it as "Verified",
 * or as "Awaiting verification", would be the silent success D-2.1-4 forbids.
 * The copy itself lives in `trust-labels.ts`, beside the trust and pipeline
 * labels, so every surface reads the same words.
 */

/** The accessible name: what the badge means, with the evidence a sighted user can hover for. */
function accessibleName(verification: HumanVerification, label: string): string {
  if (verification.is_human_verified) {
    return `${label} by ${verification.verified_by_upn} at ${verification.verified_utc}`;
  }
  if (verification.verification_status === 'AutoApproved') {
    return `Auto-approved by policy at ${verification.auto_approved_utc} — not reviewed by a person`;
  }
  return verification.escalated_utc === null ? label : `${label}, escalated at ${verification.escalated_utc}`;
}

export function VerificationBadge({ verification }: { readonly verification: HumanVerification }) {
  const copy = VERIFICATION_STATUS_COPY[verification.verification_status];
  const name = accessibleName(verification, copy.label);

  return (
    // The text label is always present; the tone class only adds colour to it.
    // Status is never conveyed by colour alone (CLM-51). The verifier and the
    // timestamp ride in a visually hidden span rather than in an `aria-label`,
    // which a roleless generic element does not reliably expose — and it is
    // rendered only when it says more than the visible label already does.
    <span className={`tag ${copy.tone}`} title={name}>
      {copy.label}
      {name === copy.label ? null : <span className="vh"> — {name}</span>}
    </span>
  );
}

/** The same mapping for a bare status string, e.g. a queue row's own column. */
export function VerificationStatusLabel({ status }: { readonly status: VerificationStatus }) {
  const copy = VERIFICATION_STATUS_COPY[status];
  return <span className={`tag ${copy.tone}`}>{copy.label}</span>;
}
